import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { WEEKDAYS, isoWeek, parseISODate, todayISO } from '../utils/dates';
import SalePickerRows from '../components/SalePickerRows';
import { REMISIONES_CHANGED_EVENT } from '../utils/remisionBoxes';
import './SalesFormPage.css';

const BLANK = {
  title: 'TURNO # 1',
  sale: '', destination: '', driver: '', inspector: '',
  container_number: '', thermograph_number: '', shipping_line_seal: '', satellite_seal: '',
  farm_code: '', boxes: '', pallets: '', fruit_weight_with_carton: '',
};

const MANUAL_FIELDS = [
  { key: 'container_number', label: 'Nº CONTENEDOR' },
  { key: 'thermograph_number', label: 'Nº TERMÓGRAFO' },
  { key: 'shipping_line_seal', label: 'SELLO NAVIERA' },
  { key: 'satellite_seal', label: 'SELLO SATELITAL' },
];


export default function RemisionFormPage() {
  const { id: remisionId } = useParams();
  const isEdit = Boolean(remisionId);
  const navigate = useNavigate();
  const [original, setOriginal] = useState(null);
  const [sales, setSales] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...BLANK, date: todayISO() });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('dispatch-referrals/available-sales/'), api.get('destinations/'),
      api.get('drivers/'), api.get('inspectors/'),
      isEdit ? api.get(`dispatch-referrals/${remisionId}/`) : Promise.resolve(null),
    ])
      .then(([s, d, dr, i, r]) => {
        setSales(s.data);
        setDestinations(d.data);
        setDrivers(dr.data);
        setInspectors(i.data);
        if (r) {
          const rem = r.data;
          setOriginal(rem);
          setForm({
            title: rem.title,
            date: rem.date,
            sale: String(rem.sale),
            destination: rem.destination ? String(rem.destination) : '',
            driver: String(rem.driver),
            inspector: String(rem.inspector),
            ...Object.fromEntries(MANUAL_FIELDS.map((f) => [f.key, rem[f.key] || ''])),
            farm_code: rem.farm_code || '',
            boxes: rem.boxes != null ? String(rem.boxes) : '',
            pallets: rem.pallets != null ? String(rem.pallets) : '',
            fruit_weight_with_carton: rem.fruit_weight_with_carton != null
              ? String(Number(rem.fruit_weight_with_carton)).replace('.', ',')
              : '',
          });
        }
      })
      .catch(() => setStatus({
        type: 'error', text: isEdit ? 'No se pudo cargar la remisión.' : 'No se pudieron cargar las listas.',
      }))
      .finally(() => setLoading(false));
  }, [isEdit, remisionId]);

  const selectedDate = form.date ? parseISODate(form.date) : null;
  const dayLabel = selectedDate ? WEEKDAYS[selectedDate.getDay()] : '';
  const weekLabel = selectedDate ? isoWeek(selectedDate) : '';
  const sale = sales.find((s) => String(s.id) === form.sale);
  const catalogDriver = drivers.find((d) => String(d.id) === form.driver);
  // An edited remisión keeps the conductor details it was saved with unless the conductor changes.
  const driver = original && String(original.driver) === form.driver
    ? {
      id_number: original.driver_id_number,
      cellphone: original.driver_cellphone,
      license_plate: original.driver_license_plate,
    }
    : catalogDriver;

  const set = (field) => (e) => setForm((fm) => ({ ...fm, [field]: e.target.value }));

  // Picking a sale pre-fills Nº Cajas and Pallets from it; the user can still change both.
  const selectSale = (saleId) => {
    const picked = sales.find((s) => String(s.id) === saleId);
    setForm((fm) => ({
      ...fm,
      sale: saleId,
      boxes: picked ? String(picked.boxes) : '',
      pallets: picked ? String(picked.pallets) : '',
    }));
  };

  const digitsInput = (field) => (
    <input
      className="sales-cell-input"
      inputMode="numeric"
      value={form[field]}
      onChange={(e) => setForm((fm) => ({ ...fm, [field]: e.target.value.replace(/\D/g, '') }))}
      title="Se llena con el dato de la venta; puede cambiarlo"
    />
  );

  const cartonWeight = Number(form.fruit_weight_with_carton.replace(',', '.'));
  const isComplete = form.title.trim() && form.date && form.sale && form.driver
    && form.inspector && MANUAL_FIELDS.every((f) => form[f.key].trim())
    && form.farm_code.trim() && form.boxes !== '' && form.pallets !== ''
    && form.fruit_weight_with_carton !== '' && !Number.isNaN(cartonWeight);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isComplete) return;
    setSaving(true);
    setStatus(null);
    try {
      const payload = {
        title: form.title.trim(),
        date: form.date,
        sale: Number(form.sale),
        destination: form.destination ? Number(form.destination) : null,
        driver: Number(form.driver),
        inspector: Number(form.inspector),
        ...Object.fromEntries(MANUAL_FIELDS.map((f) => [f.key, form[f.key].trim()])),
        farm_code: form.farm_code.trim(),
        boxes: Number(form.boxes),
        pallets: Number(form.pallets),
        fruit_weight_with_carton: cartonWeight.toFixed(2),
      };
      if (isEdit) {
        await api.patch(`dispatch-referrals/${remisionId}/`, payload);
        window.dispatchEvent(new Event(REMISIONES_CHANGED_EVENT));
        navigate('/despachos/historial');
        return;
      }
      await api.post('dispatch-referrals/', payload);
      window.dispatchEvent(new Event(REMISIONES_CHANGED_EVENT));
      setStatus({ type: 'success', text: 'Remisión registrada correctamente.' });
      // Keep the date: several turnos are usually entered for the same day.
      setForm((fm) => ({ ...BLANK, date: fm.date }));
    } catch (err) {
      setStatus({ type: 'error', text: err.response?.data?.detail || 'No se pudo guardar la remisión.' });
    } finally {
      setSaving(false);
    }
  };

  const listSelect = (value, onChange, options, labelOf = (o) => o.name) => (
    <select className="sales-cell-input" value={value} onChange={onChange}>
      <option value="">— seleccionar —</option>
      {options.map((o) => <option key={o.id} value={o.id}>{labelOf(o)}</option>)}
    </select>
  );

  const row = (label, content, className = '') => (
    <tr key={label}>
      <td className="sales-label">{label}</td>
      <td className={`sales-value ${className}`}>{content}</td>
    </tr>
  );

  const fromSale = (value) => (sale ? (value || <span className="sales-muted">—</span>) : '');

  return (
    <div className="sales-form-root">
      <div className="sales-form-topbar">
        <Link to="/despachos" className="sales-back-link">← Despachos</Link>
        <Link to="/despachos/historial" className="sales-back-link">Historial de Remisiones →</Link>
      </div>

      {status && <div className={`sales-status sales-status-${status.type}`}>{status.text}</div>}

      {loading ? (
        <div className="sales-empty">Cargando…</div>
      ) : (
        <form onSubmit={handleSubmit}>
          <table className="sales-sheet remision-sheet">
            <thead>
              <tr>
                <th colSpan={2} className="sales-sheet-header remision-header">
                  <input
                    className="remision-title-input"
                    value={form.title}
                    onChange={(e) => setForm((fm) => ({ ...fm, title: e.target.value.toUpperCase() }))}
                    aria-label="Título"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {row('FECHA', (
                <input type="date" className="sales-cell-input" value={form.date} onChange={set('date')} required />
              ))}
              {row('SEMANA', weekLabel)}
              {row('DIA', dayLabel)}
              <SalePickerRows sales={sales} value={form.sale} onSelect={selectSale} date={form.date} />
              {row('EMPRESA', fromSale(sale?.company_name), 'remision-auto')}
              {row('HACIENDA', fromSale(sale?.farm_name), 'remision-auto')}
              {row('EXPORTADORA', fromSale(sale?.exporter_name), 'remision-auto')}
              {row('DESTINO', listSelect(form.destination, set('destination'), destinations))}
              {row('TRANSPORTE', fromSale(sale?.transportation_name), 'remision-auto')}
              {row('CONDUCTOR', listSelect(form.driver, set('driver'), drivers))}
              {row('CEDULA', driver ? (driver.id_number || <span className="sales-muted">—</span>) : '', 'remision-auto')}
              {row('CELULAR', driver ? (driver.cellphone || <span className="sales-muted">—</span>) : '', 'remision-auto')}
              {row('PLACA', driver ? (driver.license_plate || <span className="sales-muted">—</span>) : '', 'remision-auto')}
              {MANUAL_FIELDS.map((f) => row(f.label, (
                <input
                  className="sales-cell-input"
                  value={form[f.key]}
                  onChange={(e) => setForm((fm) => ({ ...fm, [f.key]: e.target.value.toUpperCase() }))}
                />
              ), 'sales-manual'))}
              {row('COD MAGAP', fromSale(sale?.magap), 'remision-auto')}
              {row('MARCA CAJA', fromSale(sale?.brand_name), 'remision-auto')}
              {row('COD HACIENDA', (
                <input
                  className="sales-cell-input"
                  value={form.farm_code}
                  onChange={(e) => setForm((fm) => ({ ...fm, farm_code: e.target.value.toUpperCase() }))}
                />
              ), 'sales-manual')}
              {row('Nº CAJAS', digitsInput('boxes'), 'sales-manual')}
              {row('PALLETS', digitsInput('pallets'), 'sales-manual')}
              {row('PESO FRUTA CON CARTÓN', (
                <input
                  className="sales-cell-input"
                  inputMode="decimal"
                  value={form.fruit_weight_with_carton}
                  onChange={(e) => setForm((fm) => ({ ...fm, fruit_weight_with_carton: e.target.value.replace(/[^\d.,]/g, '') }))}
                />
              ), 'sales-manual')}
              {row('PESO CAJA FRUTA', fromSale(sale && String(Number(sale.fruit_weight)).replace('.', ',')), 'remision-auto')}
              {row('TIPO DE CAJA', fromSale(sale?.box_type_name), 'remision-auto')}
              {row('NOMBRE INSPECTOR', listSelect(form.inspector, set('inspector'), inspectors))}
            </tbody>
          </table>

          <div className="sales-actions">
            {isEdit && (
              <Link to="/despachos/historial" className="sales-cancel-btn">Cancelar</Link>
            )}
            <button className="sales-save-btn" type="submit" disabled={saving || !isComplete}>
              {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Guardar remisión'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
